import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useHandleError } from '@dotfile/frontend/shared/components';
import { useRequestSignatureElectronicSignatureCheckMutation } from '@dotfile/shared/data-access-client-portal';

import { useLatestClientPortalVersion } from '../../../../shared';

export const useRequestSignature = () => {
  const handleError = useHandleError();
  const { t } = useTranslation();
  const { data } = useLatestClientPortalVersion({
    // If the data is available, it must already be in the cache
    fetchPolicy: 'cache-only',
  });
  const supportEmail =
    data?.latestClientPortalVersion?.setting?.supportEmail ??
    'support@dotfile.com';
  const errorMessage = t('errors.default.subtitle', {
    ns: 'client-portal',
    supportEmail,
    defaultValue:
      'Try refreshing the page and, if the problem persists, click below to reach out to support ({{supportEmail}}) with the following error message:',
  });

  const [requestSignatureMutation, state] =
    useRequestSignatureElectronicSignatureCheckMutation();

  const requestSignature = useCallback(
    async ({ checkId }: { checkId: string }) => {
      try {
        await requestSignatureMutation({
          variables: {
            input: {
              checkId,
            },
          },
        });
        return true;
      } catch (error) {
        (error as Error).message =
          `${errorMessage} ${(error as Error).message}`;

        handleError({
          title: 'Failed to request signature',
          error,
        });

        return false;
      }
    },
    [requestSignatureMutation, handleError, errorMessage],
  );

  return {
    requestSignature,
    ...state,
  };
};
